import React, { Component, useRef, useState } from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import Autosuggest from 'react-autosuggest';
import HttpStatus from 'http-status-codes';
import { getTab } from './OrderList';
import { THEME } from '../Autocomplete';
import { findOrder } from '../../api';
import { trimDate } from '../../helper';

const RESET_DELAY = 1000;
const AUTOCOMPLETE_DELAY = 500;

function AutoComplete({ onSubmit, onChange, fetchResults, suggestions, onSuggestionsClearRequested, ...props }) {
	const [selected, setSelected] = useState(null);
	props.onChange = (e, { newValue }) => onChange(newValue);
	props.onKeyUp = e => {
		if(e.key === 'Enter' && !selected && props.value) {
			fetchResults(props.value, 0, true);
		}
	};
	return <Autosuggest
		id="search_autocomplete"
		theme={THEME}
		suggestions={suggestions}
		onSuggestionsFetchRequested={({ value }) => {
			fetchResults(value, AUTOCOMPLETE_DELAY);
		}}
		onSuggestionsClearRequested={onSuggestionsClearRequested}
		onSuggestionSelected={(e, data) => {
			e.preventDefault();
			if(data.suggestion) {
				onSubmit(data.suggestion);
			}
		}}
		getSuggestionValue={v => v.order.code}
		onSuggestionHighlighted={setSelected}
		renderSuggestion={v => `${v.order.code} ${v.date}`}
		inputProps={props} />;
}

function SearchForm({ onSubmit, value, search, children, className, onChange }) {
	const [suggestions, setSuggestions] = useState([]);
	const timeout = useRef(0);
	const fetchResults = (v, delay, autoSubmit) => {
		clearTimeout(timeout.current);
		const t = setTimeout(() => {
			search(v).then(s => {
				if(t === timeout.current) {
					setSuggestions(s);
					if(autoSubmit && s.length <= 1) {
						onSubmit(s[0]);
					}
				}
			});
		}, delay);
		timeout.current = t;
	};
	return <form className="form-inline" onSubmit={e => {
		e.preventDefault();
		fetchResults(value, 0, true);
	}}>
		<div className="form-group">
			<AutoComplete
				suggestions={suggestions}
				onSuggestionsClearRequested={() => setSuggestions([])}
				fetchResults={fetchResults}
				type="search"
				className={className}
				placeholder="Zoek op ordernummer"
				value={value}
				required
				onSubmit={onSubmit}
				onChange={onChange} />
		</div>
		<input type="submit" className="btn btn-primary" value="Zoek" />
		{children}
	</form>;
}

class OrderSearch extends Component {
	constructor(props) {
		super(props);
		this.state = {
			search: '',
			redirect: null,
			found: null,
			hash: null
		};
		this.submit = this.submit.bind(this);
		this.timeout = 0;
	}

	async findOrder(search) {
		search = search.trim();
		if(!search) {
			return [];
		}
		if(this.props.person.role === 'CUSTOMER_SERVICE') {
			try {
				const orders = await findOrder(search);
				return orders.map(order => {
					const date = trimDate(order.dateOverride || order.shipmentDate);
					return { date, order };
				});
			} catch(err) {
				if(err?.response?.status !== HttpStatus.NOT_FOUND) {
					throw err;
				}
			}
		}
		const { byDate } = this.props;
		const matches = [];
		for(const date in byDate) {
			for(const order of byDate[date].orders) {
				if(order.code.includes(search)) {
					matches.push({ date, order });
				}
			}
		}
		return matches;
	}

	submit(result) {
		if(result) {
			const { date, order } = result;
			const id = encodeURIComponent(order._id);
			this.setState({
				found: true,
				redirect: `/${date}/${getTab(order)}?highlight=${id}`,
				hash: `#${id}`
			});
		} else {
			this.setState({
				found: false
			});
		}
		clearTimeout(this.timeout);
		this.timeout = setTimeout(() => this.setState({ found: null }), RESET_DELAY);
	}

	componentDidUpdate() {
		const { redirect, hash } = this.state;
		const { location } = this.props;
		if(redirect && location && location.pathname + location.search === redirect) {
			this.setState({ redirect: null, hash: null }, () => {
				window.location = hash;
			});
		}
	}

	render() {
		const { search, found, redirect } = this.state;
		const classes = ['form-control'];
		if(found !== null) {
			if(found) {
				classes.push('is-valid');
			} else {
				classes.push('is-invalid');
			}
		}
		return <SearchForm
			onSubmit={this.submit}
			value={search}
			className={classes.join(' ')}
			onChange={v => this.setState({ search: v })}
			search={v => this.findOrder(v)}>
			{redirect && <Redirect to={redirect} />}
		</SearchForm>;
	}
}

export default withRouter(OrderSearch);
